import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { Fade, Snackbar } from '@material-ui/core';
import { Create } from '@material-ui/icons';
import {
  ceil, compose, getOr, filter, includes, isEmpty, map, size, toNumber, toString,
} from 'lodash/fp';
import { FormattedMessage, injectIntl } from 'react-intl';
import dayjs from 'dayjs';
import {
  Button, Grid, Heading, Pagination, RetryDialog, RowsPerPageFilter, SearchField, Spinner, TableFallbackMessage,
} from '../../common/components';
import { DraftTable, YearFilter } from '../components';
import * as actions from '../actions';
import messages from '../messages';

const RETRY_DIALOG = 'RETRY_DIALOG';
const SNACKBAR = 'SNACKBAR';

const rowsPerPageOptions = map(rowCount => ({
  id: toString(rowCount),
  value: toString(rowCount),
  label: <FormattedMessage {...messages.rowsPerPageFilterLabel} values={{ num: rowCount }} />,
}), [10, 15, 20]);

const S = {
  WritingLink: styled(Button)``,
  Create: styled(Create)`
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
    color: ${({ theme }) => theme.palette.primary.contrastText};
  `,
  Count: styled.data`
    margin-left: 0.25rem;
    color: ${({ theme }) => theme.palette.secondary.main};
  `,
  CenteredGrid: styled(Grid)`
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  `,
};

const Outbox = ({ intl, match }) => {
  const [searchWord, setSearchWord] = useState('');
  const [year, setYear] = useState({ value: '0', computed: '' });
  const [rowsPerPage, setRowsPerPage] = useState('10');
  const [pageNumber, setPageNumber] = useState(1);
  const [open, setOpen] = useState(null);

  const drafts = useSelector(state => state.outbox.drafts, shallowEqual);
  const deleted = useSelector(state => state.outbox.deleted, shallowEqual);

  const dispatch = useDispatch();

  const filteredDrafts = useMemo(() => compose(
    filter(draft => (
      (searchWord === ''
        || includes(searchWord, draft.title)
        || includes(searchWord, draft.writer)
        || includes(searchWord, getOr({ issueNumber: '' }, 'number', draft).issueNumber))
      && (year.value === '0' || toString(dayjs(draft.updatedAt.toDate()).year()) === year.value)
    )),
    getOr([], 'data'),
  )(drafts), [drafts, searchWord, year]);
  const pageCount = useMemo(
    () => (ceil(size(filteredDrafts) / rowsPerPage) || 1),
    [filteredDrafts, rowsPerPage],
  );

  const fetchDrafts = useCallback(() => dispatch(actions.fetchDraftsRequest({ sid: match.params.sid })), [dispatch, match.params.sid]);
  const deleteDrafts = useCallback((...args) => dispatch(actions.deleteDraftsRequest(...args)), [dispatch]);
  const handleClose = () => setOpen(null);
  const handleSnackbarClose = () => {
    setOpen(null);

    dispatch(actions.initDeleted());
  };
  const handleSearchWordChange = useCallback(event => setSearchWord(event.target.value), []);
  const handleSearchWordClear = useCallback(() => setSearchWord(''), []);
  const handleYearChange = useCallback(event => setYear({ value: event.target.value, computed: event.target.value }), []);
  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(event.target.value);

    setPageNumber(1);
  }, []);
  const handlePageNumberClick = useCallback(event => setPageNumber(toNumber(event.currentTarget.dataset.pageNumber)), []);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts, match.params.sid]);

  useEffect(() => {
    if ((pageNumber > pageCount) && pageCount) {
      setPageNumber(toString(pageCount));
    }
  }, [pageCount, pageNumber]);

  useEffect(() => {
    if (drafts.error) {
      setOpen(RETRY_DIALOG);

      dispatch(actions.initDrafts());
    }
  }, [dispatch, drafts.error]);

  useEffect(() => {
    if (deleted.error.drafts) {
      setOpen(SNACKBAR);
    }
  }, [deleted.error]);

  return (
    <>
      <Grid container spacing={2} paddingTop="1.5rem" paddingBottom="4.5rem">
        <Grid
          container
          item
          xs={12}
          margin={{ bottom: 24 }}
        >
          <Heading>
            <FormattedMessage {...messages.outboxHeading} />
          </Heading>
        </Grid>
        <Grid container item spacing={2} margin={{ bottom: 4 }}>
          <Grid item xs={3}>
            <SearchField
              placeholder={intl.formatMessage(messages.outboxSearchFieldPlaceholder)}
              value={searchWord}
              onChange={handleSearchWordChange}
              onClear={handleSearchWordClear}
            />
          </Grid>
          <Grid container item xs={2}>
            <YearFilter drafts={drafts} value={year.computed} onChange={handleYearChange} />
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
        >
          {/* TODO: 검색 필터 방식 재논의 예정 */}
          {drafts && (
            <DraftTable
              drafts={{ loading: drafts.loading, data: filteredDrafts }}
              deleteDrafts={deleteDrafts}
              pageNumber={pageNumber}
              rowsPerPage={rowsPerPage}
              searchWord={searchWord}
              year={year}
            />
          )}
        </Grid>
        {!drafts.loading && !isEmpty(filteredDrafts) && (
          <Grid container item xs={12} justify="space-between" marginTop="2rem">
            <S.CenteredGrid item>
              <Pagination
                pageCount={pageCount}
                pageNumber={pageNumber}
                onClick={handlePageNumberClick}
              />
            </S.CenteredGrid>
            <Grid item>
              <RowsPerPageFilter
                options={rowsPerPageOptions}
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              />
            </Grid>
          </Grid>
        )}
        {!drafts.loading && isEmpty(filteredDrafts) && (
          <TableFallbackMessage>
            <FormattedMessage
              {...messages[searchWord ? 'draftTableFallbackMessageFiltered' : 'draftTableFallbackMessageNoData']}
            />
          </TableFallbackMessage>
        )}
      </Grid>
      {(deleted.loading.drafts) && <Spinner />}
      <RetryDialog open={open === RETRY_DIALOG} onClose={handleClose} onRetry={fetchDrafts}>
        목록을 불러오는데 실패하였습니다. 다시 시도하시겠습니까?
      </RetryDialog>
      <Snackbar
        autoHideDuration={3500}
        open={open === SNACKBAR}
        onClose={handleSnackbarClose}
        message="삭제에 실패하였습니다."
        TransitionComponent={Fade}
      />
    </>
  );
};

Outbox.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default injectIntl(Outbox);
